import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Truck, Loader2, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

export default function StaffLogin() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);

  const routeByRole = (role) => {
    window.location.href = role === "mover" ? "/mover" : "/admin";
  };

  useEffect(() => {
    axios
      .get(`${API}/auth/me`)
      .then(({ data }) => {
        if (data && data.role) routeByRole(data.role);
        else setChecking(false);
      })
      .catch(() => setChecking(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, form);
      toast.success(`Welcome${data.name ? `, ${data.name}` : ""}!`);
      routeByRole(data.role);
    } catch (e2) {
      toast.error("Wrong email or password.");
      setLoading(false);
    }
  };

  if (checking) return <div className="min-h-screen bg-slate-100" data-testid="staff-login-checking" />;

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 px-5 py-10" data-testid="staff-login">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-blaze shadow-lg shadow-blaze/30">
            <Truck className="h-7 w-7 text-white" />
          </div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-slate-900">
            MOVERS<span className="text-blaze">NEARBY</span>
          </h1>
          <p className="mt-1 text-sm text-slate-500">Staff sign in — Admin &amp; Movers</p>
        </div>

        <form onSubmit={submit} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.06)]">
          <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-widest text-slate-500">Email</label>
          <input
            type="email"
            autoComplete="username"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="you@moversnearby.com.au"
            className="mb-4 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blaze focus:outline-none focus:ring-2 focus:ring-blaze/20"
            data-testid="staff-email-input"
          />
          <label className="mb-1.5 block text-[11px] font-bold uppercase tracking-widest text-slate-500">Password</label>
          <input
            type="password"
            autoComplete="current-password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder="••••••••"
            className="mb-5 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blaze focus:outline-none focus:ring-2 focus:ring-blaze/20"
            data-testid="staff-password-input"
          />
          <button
            type="submit"
            disabled={loading}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-blaze py-3 text-sm font-bold uppercase tracking-widest text-white transition-opacity disabled:opacity-60"
            data-testid="staff-login-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {loading ? "Signing in…" : "Sign in"}
          </button>
          <p className="mt-4 flex items-center justify-center gap-1.5 text-center text-[11px] text-slate-400">
            <ShieldCheck className="h-3.5 w-3.5 shrink-0" /> Admins open the dashboard, movers open their jobs — automatically.
          </p>
        </form>
      </div>
    </div>
  );
}
